import { Calendar } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ChipGroup } from './Filters';
import { Input } from './Field';
import { isoDate } from '@/lib/format';

/**
 * The date-range control every history screen filters with.
 *
 * The presets are computed with **calendar** arithmetic — `new Date(y, m, d)`,
 * which the platform normalises across month ends, leap days and daylight
 * saving. Subtracting `7 * 86400000` from "now" is wrong twice a year: on a DST
 * boundary it lands an hour early and quietly drops (or duplicates) an hour of
 * receipts at the edge of the window. On a money screen that reads as missing
 * sales, so it is worth the extra few lines.
 *
 * A week starts **Sunday**, matching how the store's own week is counted.
 *
 * Both bounds are emitted as date-only `YYYY-MM-DD` strings, and date-only is
 * exactly what the API wants: it resolves each bound against the **store's**
 * timezone, so `date_from` is store-local midnight and `date_to` covers through
 * the end of that store day. A receipt rung at 9pm belongs to the day it was
 * rung. Sending an instant instead would pin the window to whichever zone this
 * browser happens to sit in, which is the bug that was there before (TASK-0169).
 *
 * So keep emitting bare days. Nothing else is accepted now — `2026-8-3`, a bare
 * `2026-08` and an offset-less timestamp are all 400s — and nothing else can
 * leave here: every bound is either `isoDate()` output or an `<input type="date">`
 * value, both `YYYY-MM-DD` by construction.
 *
 * Which day "Today" *is* still comes off the browser's clock. For a store whose
 * admin is used in the store that is the same day the server will resolve; open
 * it from another timezone and you get your calendar day rendered in store time,
 * which is the honest answer to an ambiguous question.
 */

export type RangePreset =
  | 'today'
  | 'week'
  | 'month'
  | 'last_month'
  | 'ytd'
  | 'all'
  | 'custom';

export const RANGE_PRESETS: Array<{ value: RangePreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All' },
  { value: 'custom', label: 'Custom' },
];

export function isRangePreset(value: unknown): value is RangePreset {
  return typeof value === 'string' && RANGE_PRESETS.some((p) => p.value === value);
}

/** Local midnight of the given day — never a UTC shift. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Sunday of the week containing `d`. Negative day-of-month normalises. */
function startOfWeek(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Day 0 of month `m` is the last day of month `m - 1`. */
function endOfPreviousMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 0);
}

function startOfPreviousMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1);
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

export interface ResolvedRange {
  /** `YYYY-MM-DD`, or undefined for an open lower bound. */
  from?: string;
  /** `YYYY-MM-DD`, or undefined for an open upper bound. */
  to?: string;
}

/**
 * Turn a preset (plus the custom bounds) into the two query params.
 * `now` is injectable so the arithmetic is testable without freezing the clock.
 */
export function resolveRange(
  preset: RangePreset,
  custom?: { from?: string; to?: string },
  now: Date = new Date()
): ResolvedRange {
  const today = startOfDay(now);
  switch (preset) {
    case 'today':
      return { from: isoDate(today), to: isoDate(today) };
    case 'week':
      return { from: isoDate(startOfWeek(today)), to: isoDate(today) };
    case 'month':
      return { from: isoDate(startOfMonth(today)), to: isoDate(today) };
    case 'last_month':
      return {
        from: isoDate(startOfPreviousMonth(today)),
        to: isoDate(endOfPreviousMonth(today)),
      };
    case 'ytd':
      return { from: isoDate(startOfYear(today)), to: isoDate(today) };
    case 'all':
      return {};
    case 'custom':
      return {
        from: custom?.from || undefined,
        to: custom?.to || undefined,
      };
  }
}

/** Human sentence for the resolved window — goes under a page title. */
export function describeRange(preset: RangePreset, range: ResolvedRange): string {
  if (preset === 'all') return 'All time';
  if (!range.from && !range.to) return 'All time';
  if (range.from && range.to) {
    return range.from === range.to ? range.from : `${range.from} → ${range.to}`;
  }
  return range.from ? `From ${range.from}` : `Up to ${range.to}`;
}

/**
 * Preset chips plus the two date inputs. The inputs appear only in `custom`,
 * because two empty date boxes next to an active "Today" chip is the kind of
 * ambiguity that makes an operator distrust the number above it.
 */
export function DateRangeBar({
  preset,
  from,
  to,
  onChange,
  className,
}: {
  preset: RangePreset;
  from?: string;
  to?: string;
  onChange: (next: { preset: RangePreset; from?: string; to?: string }) => void;
  className?: string;
}) {
  const resolved = resolveRange(preset, { from, to });

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <ChipGroup<RangePreset>
        value={preset}
        onChange={(next) =>
          onChange(
            next === 'custom'
              ? // Seed the custom bounds from whatever window is on screen, so
                // switching to Custom never blanks the list.
                { preset: next, from: from ?? resolved.from, to: to ?? resolved.to }
              : { preset: next, from: undefined, to: undefined }
          )
        }
        options={RANGE_PRESETS}
      />
      {preset === 'custom' ? (
        <div className="flex items-center gap-1.5">
          <Calendar className="text-ink-faint size-3.5" aria-hidden />
          <Input
            size="sm"
            type="date"
            aria-label="From date"
            value={from ?? ''}
            max={to || undefined}
            onChange={(e) =>
              onChange({ preset: 'custom', from: e.currentTarget.value || undefined, to })
            }
            className="w-[9.5rem]"
          />
          <span className="text-2xs text-ink-faint">→</span>
          <Input
            size="sm"
            type="date"
            aria-label="To date"
            value={to ?? ''}
            min={from || undefined}
            onChange={(e) =>
              onChange({ preset: 'custom', from, to: e.currentTarget.value || undefined })
            }
            className="w-[9.5rem]"
          />
        </div>
      ) : null}
    </div>
  );
}
